/**
 * Browser storage of the HaulierMagic CoPilot chat (RMA-12, RMA-28): one key per tenant + login, so one user's chats are
 * never shown to, or uploaded as history of, the next user of the same browser; cleared on logout.
 * Pure helpers (no Angular) so they can be unit tested.
 */
import {Subject} from 'rxjs';

/** Prefix of the per-user keys (`hm-assistant-state-v2:<tenant>:<login>`). */
export const STORAGE_PREFIX = 'hm-assistant-state-v2';
/** The old single key, shared by everybody who used this browser: never read again, removed on logout. */
export const LEGACY_STORAGE_KEY = 'hm-assistant-state-v1';

/** The storage key for the signed-in account (`{tenantId, login}`), or null when there is no usable identity yet. */
export function assistantStorageKey(identity: any): string | null {
    const login = identity && typeof identity === 'object' ? String(identity.login ?? identity.email ?? identity.id ?? '').trim() : '';
    if (!login) {
        return null;
    }
    const tenant = String(identity.tenantId ?? '').trim();
    return `${STORAGE_PREFIX}:${tenant}:${login.toLowerCase()}`;
}

/** Removes every stored assistant chat of this browser (all users, the legacy key included). Never throws. */
export function clearAssistantStorage(storage?: Pick<Storage, 'length' | 'key' | 'removeItem'>): void {
    try {
        const store = storage || localStorage;
        const doomed: string[] = [LEGACY_STORAGE_KEY];
        for (let i = 0; i < store.length; i++) {
            const key = store.key(i);
            if (key && key.startsWith(STORAGE_PREFIX)) {
                doomed.push(key);
            }
        }
        doomed.forEach((key) => store.removeItem(key));
    } catch (e) {
        // storage unavailable: nothing was stored
    }
}

const loggedOut = new Subject<void>();
/** Emits when the user logs out: the open chat component drops its in-memory chats and stops its polling. */
export const assistantLoggedOut$ = loggedOut.asObservable();

/** Called by the login service on logout: clears the stored chats and tells the live chat to reset. */
export function onAssistantLogout(clearStored = true): void {
    if (clearStored) {
        clearAssistantStorage();
    }
    loggedOut.next();
}

/** Minimal shape of a stored chat session these helpers need. */
export interface StoredSession {
    key: string;
    label?: string;
    messages: any[];
    updatedAt?: number;
    sending?: boolean;
    queue?: any[];
}

export interface StoredState {
    sessions?: StoredSession[];
    activeKey?: string;
    closedKeys?: string[];
}

export interface StorageMerge {
    /** sessions after the merge (live objects kept where this tab is working on them) */
    sessions: StoredSession[];
    closedKeys: string[];
    /** keys whose messages were taken from the other tab */
    replaced: string[];
    /** keys added from the other tab */
    added: string[];
    /** keys dropped because the other tab closed them */
    dropped: string[];
}

/**
 * RMA-28: another browser tab of the same user stored new chat state (`storage` event). Per session the newer
 * `updatedAt` wins; a session this tab is running a turn in (`sending` / `queue`) is never replaced; sessions the other
 * tab added come in, sessions it closed go (unless busy here). The caller applies the result and does not write it back.
 */
export function mergeStoredSessions(local: StoredSession[], incoming: StoredState | null, localClosed: string[] = []): StorageMerge {
    const out: StorageMerge = {sessions: (local || []).slice(), closedKeys: (localClosed || []).slice(), replaced: [], added: [], dropped: []};
    if (!incoming || !Array.isArray(incoming.sessions)) {
        return out;
    }
    const busy = (s: StoredSession) => !!s.sending || !!(s.queue && s.queue.length);
    const closed = new Set<string>(out.closedKeys);
    (Array.isArray(incoming.closedKeys) ? incoming.closedKeys : []).forEach((k) => {
        if (typeof k === 'string' && !closed.has(k)) {
            closed.add(k);
            out.closedKeys.push(k);
        }
    });
    // sessions the other tab closed
    out.sessions = out.sessions.filter((s) => {
        if (closed.has(s.key) && !busy(s)) {
            out.dropped.push(s.key);
            return false;
        }
        return true;
    });
    for (const theirs of incoming.sessions) {
        if (!theirs || typeof theirs.key !== 'string' || !Array.isArray(theirs.messages) || closed.has(theirs.key)) {
            continue;
        }
        const index = out.sessions.findIndex((s) => s.key === theirs.key);
        if (index < 0) {
            out.sessions.push(theirs);
            out.added.push(theirs.key);
            continue;
        }
        const mine = out.sessions[index];
        if (!busy(mine) && (theirs.updatedAt || 0) > (mine.updatedAt || 0)) {
            out.sessions[index] = theirs;
            out.replaced.push(mine.key);
        }
    }
    return out;
}

/**
 * RMA-30 (HM half): after the server history arrived, an untouched empty tab that existed when the sync STARTED is
 * dropped ("Session 1" on a new device). A tab the user added while the sync ran is kept, and so is any tab with
 * messages, a running turn, a queue, pending attachments or the typed draft.
 */
export function sessionsToKeepAfterSync<T extends StoredSession>(
    sessions: T[],
    startKeys: Set<string>,
    hasAttachments: (s: T) => boolean,
    hasDraft: (s: T) => boolean
): T[] {
    return sessions.filter(
        (s) =>
            !startKeys.has(s.key) ||
            (s.messages && s.messages.length > 0) ||
            !!s.sending ||
            !!(s.queue && s.queue.length) ||
            hasAttachments(s) ||
            hasDraft(s)
    );
}

/** RMA-49: Enter that confirms an IME composition (CJK, dead keys) must not send. */
export function isImeComposing(event: {isComposing?: boolean; keyCode?: number} | null | undefined): boolean {
    return !!event && (event.isComposing === true || event.keyCode === 229);
}
